import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../AuthContext";
import { getUserErrorMessage } from "../utils/errors";

/* ─── Material Icon helper ───────────────────────────────────────── */

const MIcon = ({ name, className = "" }) => (
  <span className={`material-symbols-outlined ${className}`}>{name}</span>
);

const SpinnerIcon = () => (
  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
    <circle
      className="opacity-20"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="3"
    />
    <path
      className="opacity-80"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

/* ─── Main Login Component ───────────────────────────────────────── */

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isRegister, setIsRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const resetForm = useCallback(() => {
    setUsername("");
    setPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setMessage({ text: "", type: "" });
  }, []);

  const toggleMode = () => {
    setIsRegister((prev) => !prev);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ text: "", type: "" });

    /* client-side validation */
    if (username.length < 6) {
      setMessage({
        text: t("login.errors.usernameTooShort"),
        type: "error",
      });
      return;
    }

    if (isRegister && password !== confirmPassword) {
      setMessage({ text: t("login.errors.passwordsMismatch"), type: "error" });
      return;
    }

    if (password.length < 8) {
      setMessage({ text: t("login.errors.passwordTooShort"), type: "error" });
      return;
    }

    if (isRegister) {
      const hasUppercase = /[A-Z]/.test(password);
      const hasLowercase = /[a-z]/.test(password);
      const hasNumber = /[0-9]/.test(password);

      if (!hasUppercase || !hasLowercase || !hasNumber) {
        setMessage({
          text: t("login.errors.passwordComplexity"),
          type: "error",
        });
        return;
      }
    }

    setIsLoading(true);

    try {
      if (isRegister) {
        const res = await register(username, password);
        setIsRegister(false);
        resetForm();
        setMessage({
          text: res?.data?.message || t("login.registerSuccess"),
          type: "success",
        });
      } else {
        const user = await login(username, password);
        navigate(user.is_admin ? "/admin" : "/agents");
      }
    } catch (err) {
      setMessage({
        text: getUserErrorMessage(err, isRegister ? "register" : "login"),
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-bg min-h-screen flex items-center justify-center px-4 font-body">
      {/* card */}
      <div className="relative z-10 flat-card rounded-xl w-full max-w-[420px] animate-float-in">
        <div className="relative p-8 sm:p-10">
          {/* ── Logo Mark ── */}
          <div className="flex justify-center mb-8">
            <div className="w-14 h-14 rounded-lg bg-brand dark:bg-brand flex items-center justify-center shadow-brand-glow">
              <MIcon name="layers" className="text-white !text-[28px]" />
            </div>
          </div>

          {/* ── Heading ── */}
          <div className="text-center mb-8">
            <h1 className="font-display text-2xl font-semibold text-on-surface-light dark:text-on-surface tracking-tight mb-1.5">
              {isRegister ? t("login.createAccount") : t("login.welcomeBack")}
            </h1>
            <p className="text-sm text-on-surface-light-variant dark:text-on-surface-variant">
              {isRegister
                ? t("login.registerSubtitle")
                : t("login.signInSubtitle")}
            </p>
          </div>

          {/* ── Form ── */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Username */}
            <div className="relative">
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none">
                <MIcon name="person" className="sm" />
              </div>
              <input
                type="text"
                placeholder={t("login.username")}
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="auth-input w-full pl-11 pr-4 py-3 rounded-lg text-sm font-body"
              />
            </div>

            {/* Password */}
            <div className="relative">
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none">
                <MIcon name="lock" className="sm" />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                placeholder={isRegister ? t("login.createPassword") : t("login.password")}
                autoComplete={isRegister ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="auth-input w-full pl-11 pr-11 py-3 rounded-lg text-sm font-body"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant hover:text-on-surface-light dark:hover:text-on-surface transition-colors"
                tabIndex={-1}
                aria-label={showPassword ? t("login.hidePassword") : t("login.showPassword")}
              >
                <MIcon
                  name={showPassword ? "visibility_off" : "visibility"}
                  className="sm"
                />
              </button>
            </div>

            {/* Confirm Password (register only) */}
            {isRegister && (
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none">
                  <MIcon name="lock" className="sm" />
                </div>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder={t("login.confirmPassword")}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="auth-input w-full pl-11 pr-11 py-3 rounded-lg text-sm font-body"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant hover:text-on-surface-light dark:hover:text-on-surface transition-colors"
                  tabIndex={-1}
                  aria-label={showConfirmPassword ? t("login.hidePassword") : t("login.showPassword")}
                >
                  <MIcon
                    name={showConfirmPassword ? "visibility_off" : "visibility"}
                    className="sm"
                  />
                </button>
              </div>
            )}

            {/* ── Message Banner ── */}
            {message.text && (
              <div
                className={`
                  rounded-lg px-4 py-3 text-sm flex items-start gap-2.5
                  ${
                    message.type === "success"
                      ? "bg-emerald-500/10 dark:bg-emerald-400/10 border border-emerald-500/20 dark:border-emerald-400/20 text-emerald-600 dark:text-emerald-400"
                      : "bg-red-500/10 dark:bg-red-400/10 border border-red-500/20 dark:border-red-400/20 text-red-600 dark:text-red-400"
                  }
                `}
              >
                <span className="mt-0.5 shrink-0">
                  <MIcon
                    name={message.type === "success" ? "check_circle" : "error"}
                    className="sm"
                  />
                </span>
                <span>{message.text}</span>
              </div>
            )}

            {/* ── Submit Button ── */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-brand dark:bg-brand-container hover:bg-brand-light dark:hover:bg-brand text-white font-semibold py-3 rounded-lg transition-all duration-200 shadow-brand-glow disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm tracking-wide active:scale-95"
            >
              {isLoading ? (
                <>
                  <SpinnerIcon />
                  <span>{isRegister ? t("login.creatingAccount") : t("login.signingIn")}</span>
                </>
              ) : (
                <span>{isRegister ? t("login.createAccountBtn") : t("login.signIn")}</span>
              )}
            </button>
          </form>

          {/* ── Toggle Mode ── */}
          <div className="mt-8 pt-6 border-t border-surface-light-highest dark:border-white/5">
            <p className="text-center text-sm text-on-surface-light-variant dark:text-on-surface-variant">
              {isRegister ? t("login.haveAccount") : t("login.noAccount")}
              <button
                onClick={toggleMode}
                className="ml-1.5 text-brand dark:text-brand font-medium hover:text-brand-light dark:hover:text-primary transition-colors"
              >
                {isRegister ? t("login.signIn") : t("login.signUp")}
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* ── Subtle bottom attribution ── */}
      <div className="absolute bottom-6 text-center z-10">
        <span className="text-[11px] text-on-surface-light-variant dark:text-on-surface-variant tracking-widest uppercase font-display">
          Receptionist Platform
        </span>
      </div>
    </div>
  );
};

export default Login;
